import { ViewRoom } from '@xeetr/types/models'
import { useQuery } from '@tanstack/react-query'
import { useContext } from 'react'

import { ActiveTenantContext } from '../context/common/Context'
import { queryKeys } from '../lib/queryKeys'
import { RoomService } from '../services/Room.service'

import { useAuth } from './useAuth'

interface UseRoomsReturn {
  rooms: ViewRoom[]
  isLoading: boolean
}

/**
 * Hook to access tenant rooms via dedicated API endpoint.
 *
 *
 * @example
 * ```tsx
 * const { rooms, isLoading } = useRooms()
 * rooms.map(room => ...)
 * ```
 */
export const useRooms = (): UseRoomsReturn => {
  const { tenantId } = useContext(ActiveTenantContext)
  const { getAccessTokenSilently } = useAuth()

  const { data: rooms = [], isLoading } = useQuery({
    queryKey: queryKeys.rooms.all(tenantId),
    queryFn: async () => {
      const token = await getAccessTokenSilently()
      const roomService = new RoomService(token, tenantId)
      return roomService.getRooms()
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // 5 minutes - rooms don't change frequently
  })

  return { rooms, isLoading }
}
